/**
 * The default amount of milliseconds to wait before we actually issue
 * a search request.
 */
const DEFAULT_THROTTLE_MS = 250;
/**
 * Default AbortControllerFactory that returns an AbortController if
 * one exists in global scope, but returns undefined otherwise.
 */
const defaultCreateAbortController = () => {
    return typeof AbortController !== "undefined"
        ? new AbortController()
        : undefined;
};
/**
 * An error class for a non-200 HTTP status code response from the
 * seach service.
 */
export class SearchHttpStatusError extends Error {
    constructor(status) {
        super(`Received HTTP ${status}`);
        this.status = status;
    }
}
/**
 * This class can be used to issue search requests
 * based on a query whose value may change over time
 * due to e.g. keyboard input.
 */
export class SearchRequester {
    constructor(options) {
        this.options = options;
        this.requestId = 0;
        this.createAbortController =
            options.createAbortController || defaultCreateAbortController;
        this.abortController = this.createAbortController();
        this.throttleTimeout = null;
        if (!options.fetch && typeof fetch === "undefined") {
            throw new Error(`A fetch implementation was not passed to ${this.constructor.name}, ` +
                "and one does not exist in the global scope!");
        }
        this.fetch = options.fetch || fetch;
    }
    /**
     * Fetch results for the given query, returning null if the
     * network request was aborted.
     */
    fetchResults(value) {
        const url = this.searchQueryToURL(value);
        // It's important that we pull fetch out as its own variable,
        // as this will bind its "this" context to the global scope
        // when it's called, which is important for most/all window.fetch()
        // implementations.
        const { fetch } = this;
        return fetch(url, {
            signal: this.abortController && this.abortController.signal,
        })
            .then((res) => {
            if (res.status !== 200) {
                throw new SearchHttpStatusError(res.status);
            }
            return res.json();
        })
            .catch((e) => {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Don't worry about it, the user just aborted the request.
                if (this.options.onAbort) {
                    this.options.onAbort(value);
                }
                return null;
            }
            else {
                throw e;
            }
        });
    }
    /**
     * Fetch results for the given query, returning null if the
     * query was superseded by a newer one.
     */
    async fetchResultsForLatestRequest(value) {
        const originalRequestId = this.requestId;
        let results = await this.fetchResults(value);
        if (this.requestId === originalRequestId) {
            return results;
        }
        return null;
    }
    /**
     * Abort any currently in-flight requests.
     */
    resetSearchRequest() {
        if (this.throttleTimeout !== null) {
            clearTimeout(this.throttleTimeout);
            this.throttleTimeout = null;
        }
        this.requestId++;
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = this.createAbortController();
        }
    }
    /**
     * Change the current search request to a new query. Return
     * whether the new query is non-empty.
     */
    changeSearchRequest(value) {
        this.resetSearchRequest();
        if (value.length > 0) {
            this.throttleTimeout = setTimeout(() => {
                this.fetchResultsForLatestRequest(value)
                    .catch(this.options.onError)
                    .then((results) => {
                    if (results) {
                        this.options.onResults(results);
                    }
                });
            }, this.options.throttleMs || DEFAULT_THROTTLE_MS);
            return true;
        }
        return false;
    }
    /**
     * Clean up all resources used by the requester.
     */
    shutdown() {
        this.resetSearchRequest();
    }
}
//# sourceMappingURL=search-requester.js.map